/**
 * Person names on a US state formation filing and on the IRS SS-4.
 *
 * Owner rule V5 (2026-09-21): English letters only, plus space, hyphen and
 * apostrophe. Wyoming's and Delaware's filing systems take Latin input only,
 * and the IRS name control has no accented or non-Latin letters. A name that
 * fails here would have to be rewritten by hand at filing time, and the
 * rewritten form would then no longer match what the customer typed anywhere
 * else.
 *
 * Periods are deliberately absent: the IRS drops them anyway, so "Jr." is
 * written "Jr" (the modal says so in its helper text).
 */

// The specified class escapes the hyphen. It is literal at the end of the class,
// which is why recommended eslint calls the escape useless.
// eslint-disable-next-line no-useless-escape
const ENGLISH_PERSON_NAME = /^[A-Za-z][A-Za-z '\-]*$/;

/** The Combining Diacritical Marks block, which NFD splits accents into. */
const COMBINING_MARKS = /[̀-ͯ]/g;

/** Latin letters that do not decompose under NFD, and curly apostrophes. */
const SPECIAL_LATIN: Readonly<Record<string, string>> = {
  ß: "ss",
  ẞ: "SS",
  æ: "ae",
  Æ: "AE",
  œ: "oe",
  Œ: "OE",
  ø: "o",
  Ø: "O",
  ł: "l",
  Ł: "L",
  đ: "d",
  Đ: "D",
  ð: "d",
  Ð: "D",
  þ: "th",
  Þ: "Th",
  ı: "i",
  ħ: "h",
  Ħ: "H",
  "’": "'",
  "‘": "'",
  ʼ: "'",
};

export function isEnglishPersonName(value: string | null | undefined): boolean {
  if (typeof value !== "string") return false;
  return ENGLISH_PERSON_NAME.test(value.trim());
}

/**
 * Best-effort Latin transliteration: strip diacritics (Ş→S, ü→u, é→e) and map
 * the handful of letters NFD cannot split. Non-Latin scripts are returned as
 * they are, because there is no single right romanization to guess at.
 */
export function transliterateToEnglish(value: string): string {
  let out = "";
  for (const character of value.normalize("NFD").replace(COMBINING_MARKS, "")) {
    out += SPECIAL_LATIN[character] ?? character;
  }
  return out.normalize("NFC");
}

/**
 * The spelling to offer the customer, or null when there is nothing useful to
 * offer (already valid, or a script transliteration cannot fix).
 */
export function englishNameSuggestion(
  value: string | null | undefined,
): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || isEnglishPersonName(trimmed)) return null;
  const candidate = transliterateToEnglish(trimmed).replace(/\s+/g, " ");
  return isEnglishPersonName(candidate) ? candidate : null;
}

/** Rulebook copy for {@link RULE_CODES.SHAREHOLDER_NAME_INVALID}; never carries the value. */
export function personNameMessage(fieldLabel: string): string {
  return `${fieldLabel} must use English letters (A-Z) only, plus spaces, hyphens and apostrophes. US state filings cannot accept other characters.`;
}
